import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    IconAlertTriangleSharp,
    IconArrowsArrowRight,
    IconCheckMark,
    IconCircleBlockLeft,
    IconMegaphone,
    type TStyle,
    useClassnames
} from '@wb-tech/ui-react';

import { analyticSDK } from '#analytics/wbsdk-analytic';
import { useGetApiV6ActiveBannerQuery } from '#adapter/api/campaigns/v6/active-banner';

import { E_BANNER_TYPE, TBannerType } from './types';
import { useBannerKey } from './use-banner-key';

import style from './index.module.pcss';

interface IBannerMessageProps {
    className?: string | TStyle
}

export const BannerMessage = (props: IBannerMessageProps) => {
    const cn = useClassnames(style, props.className);
    const bannerKey = useBannerKey();

    const { data: banners } = useGetApiV6ActiveBannerQuery({});

    const bannerData = bannerKey ? banners?.[bannerKey] : null;

    useEffect(() => {
        if(bannerData) {
            analyticSDK.bannerS({
                bid  : bannerData.id,
                title: bannerData.title,
                type : bannerData.type
            });
        }
    }, [bannerData]);

    const getIcon = useCallback((type: TBannerType) => {
        const svgProps = {
            className: cn('banner-message__title-icon', `banner-message__title-icon_${type}`)
        };

        switch (type) {
            case E_BANNER_TYPE.PROMOTION:
                return <IconMegaphone svg={svgProps} />;
            case E_BANNER_TYPE.ALERT:
                return <IconAlertTriangleSharp svg={svgProps} />;
            case E_BANNER_TYPE.ERROR:
                return <IconCircleBlockLeft svg={svgProps} />;
            case E_BANNER_TYPE.SUCCESS:
                return <IconCheckMark svg={svgProps} />;

            default: return null;
        }
    }, [cn]);

    if(!bannerData?.isPosted || !bannerKey) {
        return null;
    }

    return (
        <div
            className={cn('banner-message', `banner-message_${bannerData.type}`)}
        >
            <div className={cn('banner-message__wrapper')}>
                <div className={cn('banner-message__content')}>
                    <div className={cn('banner-message__title-container')}>
                        {bannerData?.type && getIcon(bannerData?.type)}
                        <h2 className={cn('banner-message__title', `banner-message__title_${bannerData.type}`)}>
                            {bannerData?.title}
                        </h2>
                    </div>
                    <p className={cn('banner-message__text')}>
                        {bannerData?.content}
                    </p>
                </div>
                {bannerData?.button && (
                    <div className={cn('banner-message__link-container')}>
                        <Link
                            to={bannerData?.button.link}
                            target="_blank"
                            onClick={() => analyticSDK.bannerMoreT({
                                bid  : bannerData.id,
                                title: bannerData.title,
                                type : bannerData.type
                            })}
                            className={cn('banner-message__link', `banner-message__link_${bannerData.type}`)}
                        >
                            {bannerData?.button.text}
                            <IconArrowsArrowRight
                                svg={{
                                    className: cn('banner-message__link-arrow', `banner-message__link-arrow_${bannerData.type}`)
                                }}
                            />
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};
